import { useState, type MouseEvent } from 'react'
import { AppBar, Toolbar, Typography, Box, Button, Menu, MenuItem, Divider } from '@mui/material'
import type { AppBarProps } from '@mui/material'
import { LogOut, Globe, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/features/auth'

interface TopBarProps {
    position?: AppBarProps['position']
    title?: string
}

const TopBar = ({ position = 'static', title = 'Haushaltsfinanzen' }: TopBarProps) => {
    const { t, i18n } = useTranslation()
    const { user, logout } = useAuthStore()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [langAnchorEl, setLangAnchorEl] = useState<HTMLElement | null>(null)

    const handleProfileOpen = (e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)
    const handleProfileClose = () => setAnchorEl(null)
    const handleLangOpen = (e: MouseEvent<HTMLButtonElement>) => setLangAnchorEl(e.currentTarget)
    const handleLangClose = () => setLangAnchorEl(null)

    const handleLogout = () => { logout(); handleProfileClose() }
    const changeLanguage = (lng: string) => { void i18n.changeLanguage(lng); handleLangClose() }

    return (
        <AppBar
            position={position}
            sx={{
                bgcolor: '#fafafa',
                color: 'text.primary',
            }}
        >
            <Toolbar sx={{ justifyContent: 'space-between', minHeight: '56px !important', px: 2 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.02em', color: 'text.secondary' }}>
                    {title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={handleLangOpen}
                        startIcon={<Globe size={14} />}
                        endIcon={<ChevronDown size={12} />}
                        sx={{ minWidth: 0, px: 1.5 }}
                    >
                        {i18n.language === 'de' ? 'DE' : 'EN'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleProfileOpen}
                        endIcon={<ChevronDown size={12} />}
                        sx={{ minWidth: 0, px: 1.5 }}
                    >
                        {user?.name || user?.email || t('user')}
                    </Button>
                </Box>
            </Toolbar>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 160, mt: 0.5 } } }}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        {user?.email || ''}
                    </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main', gap: 1 }}>
                    <LogOut size={14} />
                    {t('logout')}
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 120, mt: 0.5 } } }}
            >
                <MenuItem onClick={() => changeLanguage('en')} selected={i18n.language === 'en'}>English</MenuItem>
                <MenuItem onClick={() => changeLanguage('de')} selected={i18n.language === 'de'}>Deutsch</MenuItem>
            </Menu>
        </AppBar>
    )
}

export default TopBar
